import { Controller } from "@hotwired/stimulus"

/**
 * Toggle Badge Controller
 *
 * Manages loading states for toggle badge components during Turbo requests.
 * Shows spinner and "Updating..." text while request is in-flight.
 * Disables button to prevent duplicate submissions.
 *
 * Targets:
 * - button: The submit button element
 * - icon: The icon element to show spinner
 * - label: The text label to show loading text
 *
 * Values:
 * - loadingText: Text to display during loading (default: "Mise à jour...")
 * - originalLabel: Stores original label text for reset on error
 * - originalIconClass: Stores original icon classes for reset on error
 */
export default class extends Controller {
  static targets = ["button", "icon", "label"]
  static values = {
    loadingText: { type: String, default: "Mise à jour..." },
    originalLabel: String,
    originalIconClass: String
  }

  connect() {
    // Store original values for reset on error
    this.originalLabelValue = this.labelTarget.textContent
    this.originalIconClassValue = this.iconTarget.className

    // Listen for Turbo events on the form element
    this.formElement.addEventListener("turbo:submit-start", this.handleSubmitStart.bind(this))
    this.formElement.addEventListener("turbo:submit-end", this.handleSubmitEnd.bind(this))
  }

  disconnect() {
    this.formElement.removeEventListener("turbo:submit-start", this.handleSubmitStart)
    this.formElement.removeEventListener("turbo:submit-end", this.handleSubmitEnd)
  }

  get formElement() {
    return this.element.closest("form")
  }

  handleSubmitStart() {
    this.showLoading()
  }

  handleSubmitEnd() {
    // Turbo Stream will replace the component if successful
    // If we reach here, request completed but component wasn't replaced (error case)
    // Reset to original state after brief delay
    setTimeout(() => {
      if (this.hasButtonTarget) {
        this.hideLoading()
      }
    }, 100)
  }

  showLoading() {
    this.buttonTarget.disabled = true
    this.iconTarget.className = "fa-solid fa-spinner fa-spin"
    this.labelTarget.textContent = this.loadingTextValue
  }

  hideLoading() {
    this.buttonTarget.disabled = false
    this.iconTarget.className = this.originalIconClassValue
    this.labelTarget.textContent = this.originalLabelValue
  }
}
